import { useState, useEffect, useCallback } from "react";
import { admin, adminPlans, type AdminUser, type Campaign, type Subscriber, type PricingPlan } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Spinner from "@/components/Spinner";
import { Search, Shield, Ban, CheckCircle, X, ChevronDown, ExternalLink } from "lucide-react";
import clsx from "clsx";

function UserDetailDrawer({ user, onClose, onAction }: {
  user: AdminUser; onClose: () => void;
  onAction: (action: string, value?: string) => Promise<void>;
}) {
  const [camps,  setCamps]  = useState<Campaign[]>([]);
  const [subs,   setSubs]   = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing]   = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([admin.userCampaigns(user.id), admin.userSubscribers(user.id)])
      .then(([c, s]) => { setCamps(c.campaigns); setSubs(s.subscribers); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user.id]);

  const act = async (action: string, value?: string) => {
    setActing(action);
    try { await onAction(action, value); }
    finally { setActing(null); }
  };

  const inp = "w-full px-3 py-2 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-red-300";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-black/[0.06] px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-red-500 font-bold text-sm flex-shrink-0">
              {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "?"}
            </div>
            <div>
              <p className="font-bold text-ink text-sm">{user.name || "—"}</p>
              <p className="text-xs text-slate">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-slate hover:text-ink hover:bg-black/[0.04]">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Status + Plan row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-black/[0.02] rounded-xl p-3">
              <p className="text-[10px] font-bold text-slate uppercase tracking-wider mb-2">Status</p>
              <div className={clsx("flex items-center gap-1.5 text-xs font-semibold",
                user.is_disabled ? "text-red-600" : "text-green-600")}>
                {user.is_disabled
                  ? <><Ban size={13} /> Disabled</>
                  : <><CheckCircle size={13} /> Active</>
                }
              </div>
            </div>
            <div className="bg-black/[0.02] rounded-xl p-3">
              <p className="text-[10px] font-bold text-slate uppercase tracking-wider mb-2">Admin</p>
              <div className={clsx("flex items-center gap-1.5 text-xs font-semibold",
                user.is_admin ? "text-red-600" : "text-slate")}>
                {user.is_admin ? <><Shield size={13} /> Yes</> : "No"}
              </div>
            </div>
          </div>

          {/* Plan editor */}
          <div>
            <p className="text-xs font-bold text-slate uppercase tracking-wider mb-2">Plan</p>
            <div className="flex gap-2">
              {dbPlans.map((pl) => (
                <button key={pl.slug} disabled={acting === `plan-${pl.slug}`}
                  onClick={() => act(`plan-${pl.slug}`, pl.slug)}
                  className={clsx(
                    "flex-1 py-2 rounded-xl text-xs font-semibold capitalize transition-all border",
                    user.plan === pl.slug
                      ? "bg-ink text-white border-ink"
                      : "bg-white text-slate border-black/[0.08] hover:border-black/[0.16]"
                  )}>
                  {pl.name}
                </button>
              ))}
            </div>
          </div>

          {/* Usage */}
          <div>
            <p className="text-xs font-bold text-slate uppercase tracking-wider mb-3">Usage this month</p>
            <div className="space-y-3">
              {[
                { label: "Subscribers", used: user.active_subscribers || 0, limit: user.subscriber_limit || 1000 },
                { label: "Emails sent", used: user.emails_sent_this_month || 0, limit: user.monthly_email_limit || 15000 },
              ].map(({ label, used, limit }) => {
                const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                return (
                  <div key={label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate">{label}</span>
                      <span className="font-semibold">{used.toLocaleString()} / {limit >= 999999 ? "∞" : limit.toLocaleString()}</span>
                    </div>
                    <div className="h-2 bg-black/[0.04] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-400" : "bg-teal"}`}
                        style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Joined + SMTP */}
          <div className="text-xs text-slate space-y-1.5">
            {user.created_at && (
              <div className="flex gap-2">
                <span className="text-slate/60 w-20 flex-shrink-0">Joined</span>
                <span>{new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
              </div>
            )}
            {user.smtp_user && (
              <div className="flex gap-2">
                <span className="text-slate/60 w-20 flex-shrink-0">SMTP</span>
                <span className="font-mono">{user.smtp_user}</span>
              </div>
            )}
          </div>

          {/* Recent campaigns */}
          {!loading && camps.length > 0 && (
            <div>
              <p className="text-xs font-bold text-slate uppercase tracking-wider mb-2">Campaigns</p>
              <div className="space-y-1.5">
                {camps.slice(0, 4).map((c) => (
                  <div key={c.id} className="flex items-center justify-between bg-black/[0.02] rounded-xl px-3 py-2.5">
                    <p className="text-xs font-medium text-ink truncate flex-1 pr-3">{c.name}</p>
                    <StatusBadge status={c.status} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 pt-2">
            <button
              onClick={() => act(user.is_disabled ? "enable" : "disable")}
              disabled={!!acting}
              className={clsx(
                "w-full py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-50",
                user.is_disabled
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
              )}>
              {acting === "enable" || acting === "disable"
                ? "Working…"
                : user.is_disabled ? "Enable account" : "Disable account"
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const [users, setUsers]       = useState<AdminUser[]>([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch]     = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [acting, setActing]     = useState<string | null>(null);
  const [dbPlans, setDbPlans]   = useState<PricingPlan[]>([]);

  useEffect(() => {
    adminPlans.list().then((d) => setDbPlans(d.plans)).catch(console.error);
  }, []);

  // Build plan limits map from DB plans
  const PLAN_LIMITS: Record<string, { subscriber_limit: number; monthly_email_limit: number }> = {};
  for (const p of dbPlans) {
    PLAN_LIMITS[p.slug] = {
      subscriber_limit:    p.subscriber_limit ?? 999999,
      monthly_email_limit: p.monthly_email_limit ?? 999999,
    };
  }
  const PLANS = ["", ...dbPlans.map((p) => p.slug)];

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await admin.users({ search: search || undefined, plan: planFilter || undefined, limit: 50 });
      setUsers(res.users); setTotal(res.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, planFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (userId: string, action: string, value?: string) => {
    setActing(userId);
    try {
      if (action === "disable")    await admin.disableUser(userId);
      else if (action === "enable") await admin.enableUser(userId);
      else if (action.startsWith("plan-") && value) {
        await admin.updateUser(userId, { plan: value, ...PLAN_LIMITS[value] });
      }
      load();
      if (selected?.id === userId) setSelected(null);
    } catch (e: any) { alert(e.message || "Action failed"); }
    finally { setActing(null); }
  };

  const planColor: Record<string, string> = {
    starter:    "bg-black/[0.04] text-slate",
    growth:     "bg-coral/10 text-coral",
    enterprise: "bg-teal/10 text-teal",
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-lg sm:text-xl font-bold text-ink">Users</h1>
        <p className="text-xs sm:text-sm text-slate mt-0.5">{total.toLocaleString()} total users</p>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">
        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/[0.08] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
          </div>
          <div className="flex gap-1.5 p-1 bg-black/[0.04] rounded-xl">
            {PLANS.map((p) => (
              <button key={p || "all"} onClick={() => setPlanFilter(p)}
                className={clsx("px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all",
                  planFilter === p ? "bg-white text-ink shadow-sm" : "text-slate hover:text-ink")}>
                {p || "All plans"}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Spinner className="py-20" /> : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-black/[0.06]">
                    {["User", "Plan", "Status", "Subscribers", "Emails (mo)", "Joined", ""].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}
                      className="border-b border-black/[0.04] hover:bg-black/[0.01] cursor-pointer transition-colors"
                      onClick={() => setSelected(u)}>
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-ink truncate max-w-[180px]">{u.name || "—"}</p>
                        <p className="text-xs text-slate truncate max-w-[180px]">{u.email}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={clsx("px-2.5 py-1 rounded-full text-xs font-semibold capitalize", planColor[u.plan] || planColor.starter)}>
                          {u.plan}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {u.is_disabled
                          ? <span className="flex items-center gap-1 text-xs font-semibold text-red-500"><Ban size={12} /> Disabled</span>
                          : <span className="flex items-center gap-1 text-xs font-semibold text-green-600"><CheckCircle size={12} /> Active</span>
                        }
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate">{(u.active_subscribers || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-sm text-slate">{(u.emails_sent_this_month || 0).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-xs text-slate whitespace-nowrap">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "—"}
                      </td>
                      <td className="px-5 py-3.5">
                        <ExternalLink size={13} className="text-slate/40" />
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-14 text-center text-slate text-sm">No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {users.map((u) => (
                <div key={u.id} className="bg-white rounded-2xl border border-black/[0.06] p-4"
                  onClick={() => setSelected(u)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ink text-sm truncate">{u.name || u.email}</p>
                      {u.name && <p className="text-xs text-slate truncate">{u.email}</p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={clsx("px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize", planColor[u.plan] || planColor.starter)}>
                        {u.plan}
                      </span>
                      {u.is_disabled && <Ban size={13} className="text-red-500" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {selected && (
        <UserDetailDrawer
          user={selected}
          onClose={() => setSelected(null)}
          onAction={(action, value) => handleAction(selected.id, action, value)}
        />
      )}
    </div>
  );
}
